import React, { useEffect, useState } from "react";
import Avatar from "../UserAvatar/Avatar";
import { useUserContext } from "../../UserContext";
import { generateClient } from "aws-amplify/api";
import { getUrl } from "@aws-amplify/storage";
import { getUser } from "../../graphql/queries";
import { deletePost } from "../../graphql/mutations";
import { Link } from "react-router-dom";
import "./Post.css";

interface PostProps {
  post: {
    id: string;
    userId: string;
    username: string;
    avatarUrl?: string | null;
    content: string;
    mood?: string;
    imageUrl?: string;
    createdAt: string;
  };
  onDelete?: (id: string) => void;
}

const Post: React.FC<PostProps> = ({ post, onDelete }) => {
  const { user } = useUserContext();
  const client = generateClient();

  const { id, userId, username, content, mood, imageUrl, createdAt } = post;

  const [signedImageUrl, setSignedImageUrl] = useState<string | null>(null);
  const [latestAvatar, setLatestAvatar] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false); // 👈 NEW

  useEffect(() => {
    const fetchSignedImage = async () => {
      if (!imageUrl) return;
      try {
        const { url } = await getUrl({ key: imageUrl });
        setSignedImageUrl(url.href);
      } catch (err) {
        console.error("Error fetching signed image URL:", err);
      }
    };

    const fetchLatestAvatar = async () => {
      try {
        const result: any = await client.graphql({
          query: getUser,
          variables: { id: userId },
        });
        setLatestAvatar(result.data.getUser?.avatarUrl || null);
      } catch (err) {
        console.error("Error fetching latest avatar:", err);
      }
    };

    fetchSignedImage();
    fetchLatestAvatar();
  }, [imageUrl, userId]);

  const formattedDate = new Date(createdAt).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
  const formattedTime = new Date(createdAt)
    .toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
    .toLowerCase();

  const handleDelete = async () => {
    const confirm = window.confirm(
      "Are you sure you want to delete this post?"
    );
    if (!confirm) return;

    setIsDeleting(true); // 👈 NEW

    try {
      await client.graphql({
        query: deletePost,
        variables: { input: { id } },
      });

      if (onDelete) onDelete(id);
    } catch (err) {
      console.error("Error deleting post:", err);
    } finally {
      setIsDeleting(false); // 👈 NEW
    }
  };

  return (
    <div className="feed-post">
      <div className="post-header">
        <div className="post-avatar">
          <Link to={`/profile/${userId}`} className="avatar-link">
            <Avatar
              avatarUrl={latestAvatar}
              name={username}
              size={40}
              className="avatar-img"
            />
          </Link>
          <div className="avatar-text">
            <Link to={`/profile/${userId}`} className="avatar-name">
              {username}
            </Link>
            <span className="post-time">
              {formattedDate} at {formattedTime}
            </span>
          </div>
        </div>
        {mood && <span className="post-mood">{mood}</span>}
      </div>
      <p className="post-content">{content}</p>
      {signedImageUrl && (
        <img src={signedImageUrl} alt="post" className="post-image" />
      )}
      <div className="post-footer">
        {user?.id === userId && (
          <span
            className={`delete-post-link ${isDeleting ? "disabled" : ""}`}
            onClick={isDeleting ? undefined : handleDelete}
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </span>
        )}
      </div>
    </div>
  );
};

export default Post;
